import './App.css';
import { useEffect, useRef } from 'react';
import { isPast, isFuture, closestIndexTo, isWithinInterval, sub } from "date-fns";

/**
 * Renders a salary schedule component based on provided props.
 * 
 * This component calculates and displays a salary progression schedule,
 * including step increases, anniversaries, and longevity bonuses if applicable.
 * 
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.appointedDate - The initial appointment date.
 * @param {string} props.equateDate - The equate date for salary calculations.
 * @param {number} props.termLength - The length of the term in milliseconds.
 * @param {string} props.stepCode - The initial step code (e.g., "1A").
 * @param {number} props.terms - The number of terms to calculate.
 * @param {boolean} props.includeLongevity - Whether to include longevity bonuses.
 * @param {string} [props.title] - Optional title for the salary schedule.
 * @param {string} [props.content] - Optional content to display above the schedule.
 * 
 * @returns {JSX.Element} A React component displaying the salary schedule.
 */
function SalarySchedule(props) {
    // Date Variables
    const formateDate = { year: 'numeric', month: 'short', day: 'numeric' };
    const appointedDate = new Date(props.appointedDate);
    let appointedDateYear = appointedDate.getFullYear();
    let equateDate = new Date(props.equateDate);
    let currentDate = new Date();
    let maxTerms = 15;
    if(props.jobTitle == "school-psychologist" || props.jobTitle == "social-worker"){
        maxTerms = 13;
    }
    if(props.jobTitle == "lab-tech"){
        maxTerms = 10;
    }
    const stepIncreaseDate = new Date();
    stepIncreaseDate.setMonth(2);
    stepIncreaseDate.setDate(1);
    stepIncreaseDate.setFullYear(appointedDate.getFullYear());
    stepIncreaseDate.setHours(0);
    stepIncreaseDate.setMinutes(0);
    stepIncreaseDate.setSeconds(0);
    stepIncreaseDate.setMilliseconds(0);
    let totalTermLength = props.termLength;
    let termLengthDifference = appointedDate.getTime() - totalTermLength;
    if(isNaN(equateDate)){
      if(totalTermLength){
        equateDate = sub(appointedDate, { years: (totalTermLength / 2) });
      }
      else{
        equateDate = new Date(appointedDate);
      }
    }
    equateDate.setFullYear(appointedDate.getFullYear())
    let stepProgression = [];
    let futureDates = 0;
    let targetIndex = 0;
    const currentStepRef = useRef(null);
    let prevStep = parseInt(props.stepCode.substring(0, 1)) + 1;
    let lastFoundLongevityIndex = 0;
    let error = false;

    // Step Code Array
    let stepCodes = [
        ["A", "B"],
        ["V", "W"],
        ["C", "D"],
        ["G", "H"],
        ["E", "F"],
        ["I", "J"],
        ["K", "L"],
        ["M", "N"]
    ];

    // Extract step letter and number from step code
    let stepLetter = props.stepCode.substring(1);
    let stepNumber = props.stepCode.substring(0, 1);
    let stepCodeIndex;
    for(var i = 0; i < stepCodes.length; i++) {
        if(stepCodes[i].includes(stepLetter)) {
            stepCodeIndex = stepCodes[i];
        }
    }

    // Create array of dates
    const datesArray =  [
        appointedDate,
        equateDate,
        stepIncreaseDate
    ];
    
    //Sort dates into ascending order
    datesArray.sort((a, b) => a - b);
    let anniversaryOne;
    for(let i = 0; i < datesArray.length; i++){
        if(datesArray[i] === appointedDate){
            if((i + 1) >= datesArray.length){
                anniversaryOne = datesArray[0];
            }
            else{
                anniversaryOne = datesArray[i + 1];
            }
        }
    }

    let anniversaryTwo = anniversaryOne === equateDate ? stepIncreaseDate : equateDate;

    // Switch anniversaryOne and anniversaryTwo if anniversaryOne is the same as appointed date
    if(anniversaryOne.getTime() === appointedDate.getTime() || anniversaryOne.getTime() === appointedDate.getTime()){
        let tempDate = anniversaryOne;
        anniversaryOne = anniversaryTwo;
        anniversaryTwo = tempDate;
    }

    let longevityCounter = 0;
    for(let i = (props.terms + 1); i <= maxTerms; i++) {
        if(i % 2 === 0) {
            longevityCounter ++;
            stepNumber++;
            if(props.includeLongevity){
                stepProgression.push(stepNumber + stepCodeIndex[0])
            }
            else{
                if(longevityCounter >= 5){
                    stepProgression.push(stepNumber + "V")
                }
                else{
                    stepProgression.push(stepNumber + stepCodeIndex[0])
                }
            }
        }
        else{
            if(props.includeLongevity){
                stepProgression.push(stepNumber + stepCodeIndex[1])
            }
            else{
                if(longevityCounter >= 5){
                    stepProgression.push(stepNumber + "W")
                }
                else{
                    stepProgression.push(stepNumber + stepCodeIndex[1])
                }
            }
        }
    }
    
    let longevityTable = [5, 10, 13, 15, 18, 20, 22];
    if(props.jobTitle == "paraprofessionals"){
        longevityTable = [5, 15];
    }
    let longevityDateTable = [];
    for(i = 0; i < longevityTable.length; i++){
        const longevity = new Date(props.appointedDate);
        let currentLongevity = longevity.setFullYear(longevity.getFullYear() + longevityTable[i]);

        longevityDateTable.push(new Date(currentLongevity));
    }

    // Map through step status object and set corresponding step status
    if(props.currentStep == 4){
        let stepStatus = Object.entries(props.stepStatus).map(([key, value]) => {
            if(key == 'step-2-status' || key == 'step-3-status'){
                if(value == 'fail'){
                    error = true;
                }
            }
        })
    }

    // Calculate targetIndex outside of the map function
    useEffect(() => {
        const tableData = currentStepRef.current.querySelectorAll("tr td:last-of-type");
        const tableRow = currentStepRef.current.querySelectorAll("tr");
        const tableDataDatesArray = [];
        for(let i = 0; i < tableData.length; i++){
            tableDataDatesArray.push(new Date(tableData[i].textContent));
            if(isPast(new Date(tableData[i].textContent))){
                tableRow[i].classList.add('completed');
            }
            if(equateDate.getDate() === stepIncreaseDate.getDate() && i % 2 != 0){
                if(equateDate.getMonth() === stepIncreaseDate.getMonth() && i % 2 != 0){
                    tableRow[i].classList.add('hidden');
                }
            }
        }
        const currentDateResults = closestIndexTo(currentDate, tableDataDatesArray)
        if(isFuture(new Date(tableDataDatesArray[currentDateResults]))){
            tableRow[currentDateResults - 1].classList.add('current');
        }
        else{
            tableRow[currentDateResults].classList.add('current');
        }

        // Update display of current date in table to always show the first of the month
        for(let i = 0; i < tableDataDatesArray.length; i++){
            var firstOfMonth = new Date(tableDataDatesArray[i].setDate(1));
            tableDataDatesArray[i].innerHtml = firstOfMonth.toLocaleDateString("en-US", formateDate);
        }
        for(let i = 0; i < tableData.length; i++){
            if(i != 0){
                tableData[i].textContent = tableDataDatesArray[i].toLocaleDateString("en-US", formateDate);
            }
        }
    }, [targetIndex]);
    
  return (
    <>
    <div className={props.includeLongevity && 'content-box salary-schedule'}>
        {props.title && <h3 className='title'>{props.title}</h3>}
        {props.content && <p className='content' dangerouslySetInnerHTML={{__html: props.content}}></p>}
        {error == true && <div className="alert-wrapper"><div className='alert warning'><p className='content'>Possible Error</p></div></div>}
        <div className="salary-schedule-container">
        <table className='salary-schedule' ref={currentStepRef}>
        {/* Step Code and Appointed Date*/}
        <tr data-index="0">
            <td>Step {props.stepCode}</td>
            <td></td>
            <td>{appointedDate.toLocaleDateString("en-US", formateDate)}</td>
        </tr>
        {/* Loop = Step Code + 1 term || Next Anniversary Date (march date or equate date) */}
            {stepProgression.map((step, index) => {
                let longevityRow = [];
                let longevityRows = [];
                let hasSalaryIncrease = false;
                let stepWithLongevity = step;
                if(anniversaryOne < appointedDate){
                    appointedDateYear++;
                }
                if (index % 2 === 0 && index !== 0) {
                    appointedDateYear++;
                }
            
                // Set the year for anniversaryOne
                anniversaryOne.setFullYear(appointedDateYear);
            
                // Set the year for anniversaryTwo
                anniversaryTwo.setFullYear(appointedDateYear);
            
                // Ensure anniversaryTwo is always later in the year than anniversaryOne
                if (anniversaryTwo < anniversaryOne) {
                    anniversaryTwo.setFullYear(appointedDateYear + 1);
                }

                if(currentDate < anniversaryOne || currentDate < anniversaryTwo){
                    futureDates++;
                }
                if(index === (stepProgression.length - 1)){
                    targetIndex = stepProgression.length - futureDates - 1;
                }

                if(step.substring(0, 1) >= 6){
                    hasSalaryIncrease = true
                }
                else{
                    if(step.substring(0, 1) > 1){
                        if(props.stepCode.substring(0, 1) == step.substring(0, 1)){
                            hasSalaryIncrease = false
                        }
                        else{
                            if(prevStep == parseInt(step.substring(0, 1))){
                                hasSalaryIncrease = true;
                            }
                            else{
                                hasSalaryIncrease = false;
                            }
                        }
                    }
                    else{
                        hasSalaryIncrease = false
                    }
                }
                prevStep = parseInt(step.substring(0, 1)) + 1;

                if(props.includeLongevity){
                    // Compare current anniversary date to all dates in longevityDateTable
                    let found = '';
                    if(index % 2 === 0){
                        found = longevityDateTable.find((element) => (element <= anniversaryOne) && longevityDateTable.indexOf(element) == lastFoundLongevityIndex);
                    }
                    else{
                        found = longevityDateTable.find((element) => (element <= anniversaryTwo) && longevityDateTable.indexOf(element) == lastFoundLongevityIndex);
                    }
                    // if the current anniversary date is greater than any date in longevityDateTable, grab the latest date and the index
                    let indexOf = longevityDateTable.indexOf(found);
                    let plusText = " + ";
                    if(props.jobTitle == "teacher" || props.jobTitle == "adult-education-teacher"){
                        plusText = " + L";
                    }
                    if(isWithinInterval(anniversaryOne, {start: longevityDateTable[lastFoundLongevityIndex],end: longevityDateTable[lastFoundLongevityIndex - 1]})){
                        stepWithLongevity = step + plusText + longevityTable[lastFoundLongevityIndex - 1];
                    }

                    if(indexOf == lastFoundLongevityIndex){
                        lastFoundLongevityIndex++;
                        stepWithLongevity = step + plusText + longevityTable[lastFoundLongevityIndex - 1];
                    }

                    if(stepProgression.length == index + 1 && lastFoundLongevityIndex < longevityDateTable.length){
                    for(i = lastFoundLongevityIndex; i < longevityDateTable.length; i++){
                            longevityRows.push(
                                <tr key={`longevity-${i}`}>
                                    <td>Step {step}{plusText}{longevityTable[i]}</td>
                                    <td><span className='salary-raise'>$</span></td>
                                    <td>{longevityDateTable[i].toLocaleDateString("en-US", formateDate)}</td>
                                </tr>
                            );
                        }
                    }
                }

                return(
                    <>
                        <tr key={index} data-index={index + 1}>
                            <td>Step {stepWithLongevity}</td>
                            <td>{hasSalaryIncrease && <span className='salary-raise'>$</span>}</td>
                            <td>{index % 2 === 0 ? (<span> {anniversaryOne.toLocaleDateString("en-US", formateDate)}</span> ):(<span>{anniversaryTwo.toLocaleDateString("en-US", formateDate)}</span>)}</td>
                        </tr>
                        {props.includeLongevity && longevityRows}
                    </>
                )   
            })}
        </table>
        </div>
        <p className='content'><strong>$</strong> = Salary Increase</p>
    </div>
    </>
  );
}

export default SalarySchedule;